import { Button } from "@/components/ui/button";
import { GetPaginatedTendersQueryVariables, TenderSortField, SortOrder } from "@/graphql/__generated__/graphql";
import { updatePaginationState, usePaginationState } from "@/store/paginationStore";
import { ArrowUpDownIcon, ArrowUpWideNarrowIcon, ArrowDownWideNarrowIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface Props {
  displayAmount: number;
  handleSubmitClick: (_variables: GetPaginatedTendersQueryVariables) => void;
}

/**
 * TendersSortingDropdown component
 * Provides a dropdown menu for sorting tender data by different fields and orders
 *
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - Number of items to display per page
 * @param {Function} props.handleSubmitClick - Function to handle submitting the updated query variables
 * @returns {JSX.Element} Sorting dropdown component
 */
const TendersSortingDropdown = (props: Props) => {

  // Props
  const { displayAmount, handleSubmitClick } = props;

  // State
  const [sortDropdownOpen, setSortDropdownOpen] = useState<boolean>(false);

  // Hooks
  const { t } = useTranslation(["translation", "common", "components"]);

  // Pagination
  const paginationState: PaginationState["paginatedTenders"] = usePaginationState("paginatedTenders");
  const { filters, sort } = paginationState as any;

  /**
   * Handles changing the sort field and order
   * @param {SortField | null} field - The field to sort by
   * @param {SortOrder | null} order - The order to sort by (ascending or descending)
   */
  const handleSortChange = (field: TenderSortField | null, order: SortOrder | null) => {
    // Update pagination state with new sort values
    updatePaginationState({
      paginatedTenders: {
        sort: field && order ? { field, order } : null
      }
    });

    // Submit with updated sort
    const updatedVariables: GetPaginatedTendersQueryVariables = {
      first: displayAmount,
      filters,
    };

    // Set sort explicitly: use provided field and order, or null if resetting
    updatedVariables.sort = field && order ? { field, order } : null;

    handleSubmitClick(updatedVariables);
  };

  /**
   * Gets user-friendly names for sort fields
   * @param {SortField} field - The sort field to get a display name for
   * @returns {string} User-friendly name for the sort field
   */
  const getSortFieldName = (field: TenderSortField): string => {
    switch (field) {
    case TenderSortField.Revenue:
      return t("components:filters.tenders.sortfields.revenue");
    case TenderSortField.ContractStartDate:
      return t("components:filters.tenders.sortfields.contractstartdate");
    case TenderSortField.DeadlineSubmission:
      return t("components:filters.tenders.sortfields.deadlinesubmission");
    case TenderSortField.TotalCost:
      return t("components:filters.tenders.sortfields.totalcost");
    case TenderSortField.Surcharge:
      return t("components:filters.tenders.sortfields.surcharge");
    default:
      return field;
    }
  };

  return (
    <DropdownMenu open={sortDropdownOpen} onOpenChange={setSortDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <ArrowUpDownIcon className="h-4 w-4" />
          {!sort?.field && (
            t("components:filters.sorting.sort")
          )}
          {sort && (
            <>
              {getSortFieldName(sort.field as TenderSortField)}
              {sort.order === SortOrder.Asc ?
                <ArrowUpWideNarrowIcon className="h-4 w-4" />
                : <ArrowDownWideNarrowIcon className="h-4 w-4" />
              }
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[220px]">
        {/* Sort Order Section */}
        <div className="grid grid-cols-2 gap-1 p-1">
          <Button
            variant="ghost"
            size="sm"
            className={cn("justify-start col-span-1", sort?.order === SortOrder.Asc ? "bg-gray-100" : "hover:bg-accent/50")}
            onClick={() => {
              if (sort?.field) {
                handleSortChange(sort.field as TenderSortField, SortOrder.Asc);
              } else if (sort?.order === SortOrder.Asc) {
                handleSortChange(null, null);
              }
              else {
                // Default to REVENUE if no field is selected
                handleSortChange(TenderSortField.Revenue, SortOrder.Asc);
              }
            }}
          >
            <ArrowUpWideNarrowIcon className="mr-2 h-4 w-4" />
            {t("components:filters.sorting.ascending")}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className={cn("justify-start col-span-1", sort?.order === SortOrder.Desc ? "bg-gray-100" : "hover:bg-accent/50")}
            onClick={() => {
              if (sort?.field) {
                handleSortChange(sort.field as TenderSortField, SortOrder.Desc);
              } else if (sort?.order === SortOrder.Desc) {
                handleSortChange(null, null);
              } else {
                // Default to REVENUE if no field is selected
                handleSortChange(TenderSortField.Revenue, SortOrder.Desc);
              }
            }}
          >
            <ArrowDownWideNarrowIcon className="mr-2 h-4 w-4" />
            {t("components:filters.sorting.descending")}
          </Button>
        </div>

        {/* Sort Fields Section */}
        <DropdownMenuSeparator />
        {Object.values(TenderSortField).map((field) => (
          <DropdownMenuCheckboxItem
            key={field}
            className="rounded-md hover:cursor-pointer"
            checked={sort?.field === field}
            onCheckedChange={() => {
              if (sort?.order) {
                handleSortChange(field as TenderSortField, sort.order as SortOrder);
              } else if (sort?.field === field) {
                handleSortChange(null, null);
              } else {
                // Default to DESC if no order is selected
                handleSortChange(field as TenderSortField, SortOrder.Desc);
              }
            }}
            onSelect={(e) => e.preventDefault()}
          >
            {getSortFieldName(field as TenderSortField)}
          </DropdownMenuCheckboxItem>
        ))}

        {/* Reset all sorting */}
        <div className="flex items-center gap-2 p-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              handleSortChange(null, null);
              setSortDropdownOpen(false);
            }}
          >
            {t("common:buttons.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TendersSortingDropdown;
